import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getContentTypeRefId, SecurityLevelLabels } from "@vertesia/common";
import { Badge, Button, ErrorBox, FormItem, Input, Panel, SelectBox, Styles, Textarea, useFetch, useToast, useTheme } from "@vertesia/ui/core";
import { SharedPropsEditor, SyncMemberHeadsToggle, UserInfo } from "@vertesia/ui/features";
import { useUserSession } from "@vertesia/ui/session";
import { MonacoEditor, GeneratedForm, ManagedObject } from "@vertesia/ui/widgets";
import dayjs from "dayjs";
import { useContext, useMemo, useRef, useState } from "react";
import { useUITranslation } from '../../../i18n/index.js';
import { SearchContext } from "../objects/search/DocumentSearchContext";
import { SelectContentType, stringifyTableLayout } from "../types";
export function EditCollectionView({ refetch, collection }) {
    const { t } = useUITranslation();
    const typeId = collection.type ? getContentTypeRefId(collection.type) : undefined;
    const tableLayoutRef = useRef(undefined);
    const toast = useToast();
    const { theme } = useTheme();
    const { client } = useUserSession();
    const search = useContext(SearchContext);
    const [isUpdating, setUpdating] = useState(false);
    const [metadata, setMetadata] = useState({
        name: collection.name,
        description: collection.description || "",
        query: collection.query ? JSON.stringify(collection.query, null, 2) : "",
        tags: collection.tags || [],
        type: collection.type ? getContentTypeRefId(collection.type) : "",
        allowed_types: collection.allowed_types || [],
        sensitivity: collection.sensitivity,
        compartments: collection.compartments || [],
    });
    const [compartmentInput, setCompartmentInput] = useState('');
    const tableLayoutValue = useMemo(() => {
        return stringifyTableLayout(collection.table_layout);
    }, [collection.table_layout]);
    const onSubmit = () => {
        let query;
        try {
            query = metadata.query ? JSON.parse(metadata.query) : undefined;
        }
        catch (err) {
            toast({
                title: t('store.invalidQueryJson'),
                description: err.message,
                status: "error",
                duration: 5000,
            });
            return;
        }
        const payload = {
            name: metadata.name,
            description: metadata.description,
            query: query,
            tags: metadata.tags,
            type: metadata.type,
            allowed_types: metadata.allowed_types,
            sensitivity: metadata.sensitivity,
            compartments: metadata.compartments,
        };
        let error;
        if (!payload.name) {
            error = t('type.nameRequired');
        }
        if (!payload.type) {
            payload.type = null;
        }
        if (error) {
            toast({
                title: t('store.validationFailed'),
                description: error,
                status: "error",
                duration: 5000,
            });
            return;
        }
        if (tableLayoutRef.current) {
            const layout = tableLayoutRef.current.getValue();
            if (layout) {
                try {
                    payload.table_layout = JSON.parse(layout);
                }
                catch (err) {
                    toast({
                        title: t('store.invalidTableLayout'),
                        description: err.message,
                        status: "error",
                        duration: 5000,
                    });
                    return;
                }
            }
            else {
                payload.table_layout = null;
            }
        }
        setUpdating(true);
        client.store.collections
            .update(collection.id, payload)
            .then(() => {
            refetch();
            // Update search query on provider if it's a dynamic collection to reflect in UI immediately
            if (collection.dynamic && search) {
                search.reset();
                void search.search();
            }
            toast({
                title: t('store.collectionUpdated'),
                description: t('store.collectionUpdatedSuccess'),
                status: "success",
                duration: 3000,
            });
        })
            .catch((err) => {
            toast({
                title: t('store.failedToUpdateCollection'),
                description: err.message,
                status: "error",
                duration: 5000,
            });
        })
            .finally(() => {
            setUpdating(false);
        });
    };
    const setField = (name, value) => {
        setMetadata({
            ...metadata,
            [name]: value,
        });
    };
    return (_jsxs("div", { className: "flex flex-col gap-4 py-2", children: [_jsxs(Panel, { title: t('store.configuration'), action: _jsx(Button, { size: "lg", isDisabled: isUpdating, onClick: onSubmit, children: t('modal.save') }), children: [_jsxs("div", { className: "flex justify-between mb-2", children: [_jsxs("div", { className: "w-1/2 gap-2 flex flex-col", children: [_jsx("div", { className: "text-sm font-medium mb-1", children: t('store.createdBy') }), _jsxs("div", { className: "gap-2 flex items-center", children: [_jsx(UserInfo, { userRef: collection.created_by, showTitle: true }), _jsxs("span", { children: ["at ", dayjs(collection.created_at).format("YYYY-MM-DD HH:mm:ss")] })] })] }), _jsxs("div", { className: "w-1/2 gap-2 flex flex-col", children: [_jsx("div", { className: "text-sm font-medium mb-1", children: t('store.updatedBy') }), _jsxs("div", { className: "gap-2 flex items-center", children: [_jsx(UserInfo, { userRef: collection.updated_by, showTitle: true }), _jsxs("span", { children: ["at ", dayjs(collection.updated_at).format("YYYY-MM-DD HH:mm:ss")] })] })] })] }), _jsx(FormItem, { label: t('type.name'), required: true, children: _jsx(Input, { value: metadata.name, onChange: (v) => setField("name", v) }) }), _jsx(FormItem, { label: t('type.description'), children: _jsx(Textarea, { value: metadata.description, onChange: (e) => setField("description", e.target.value) }) }), !collection.dynamic &&
                        _jsx(FormItem, { label: t('store.allowedContentTypes'), description: t('store.allowedContentTypesSelectDescription'), children: _jsx(SelectContentType, { defaultValue: metadata.allowed_types || null, onChange: (v) => {
                                    if (Array.isArray(v)) {
                                        setField("allowed_types", v.map(type => type.id));
                                    }
                                    else {
                                        setField("allowed_types", v ? [v.id] : []);
                                    }
                                }, isClearable: true, multiple: true }) }), collection.dynamic && (_jsx(FormItem, { label: t('store.query'), description: t('store.queryDescription'), children: _jsx(Textarea, { className: Styles.INPUT, minLines: 1, maxLines: 12, value: metadata.query, onChange: (e) => setField("query", e.target.value) }) })), _jsx(FormItem, { label: t('store.tableLayout'), description: t('store.tableLayoutDescription'), className: "h-[200px]", children: _jsx(MonacoEditor, { className: "border-1 rounded-md border-border", value: tableLayoutValue, language: "json", editorRef: tableLayoutRef, theme: theme === 'dark' ? 'vs-dark' : 'vs' }) }), _jsx(FormItem, { label: t('store.contentType'), description: t('store.typeSelectDescription'), children: _jsx(SelectContentType, { defaultValue: metadata.type || null, onChange: (v) => {
                                if (Array.isArray(v)) {
                                    setField("type", v.length > 0 ? v[0].id : null);
                                }
                                else {
                                    setField("type", v?.id || null);
                                }
                            }, isClearable: true }) }), _jsx(FormItem, { label: "Sensitivity", description: "BLP sensitivity level \u2014 propagated to member documents (max across collections)", children: _jsx(SelectBox, { options: SecurityLevelLabels.map((label, index) => ({ label: `${index} — ${label}`, value: index })), value: metadata.sensitivity !== undefined ? { label: `${metadata.sensitivity} — ${SecurityLevelLabels[metadata.sensitivity] ?? 'Unknown'}`, value: metadata.sensitivity } : undefined, onChange: (opt) => setField('sensitivity', opt.value), optionLabel: (opt) => opt.label, by: "value", placeholder: "Not set" }) }), _jsxs(FormItem, { label: "Compartments", description: "Security compartments \u2014 propagated to member documents (union across collections)", children: [_jsxs("div", { className: "flex gap-2", children: [_jsx(Input, { value: compartmentInput, onChange: setCompartmentInput, placeholder: "Add a compartment", onKeyDown: (e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                const val = compartmentInput.trim();
                                                if (val && !metadata.compartments.includes(val)) {
                                                    setField('compartments', [...metadata.compartments, val]);
                                                    setCompartmentInput('');
                                                }
                                            }
                                        } }), _jsx(Button, { type: "button", variant: "outline", onClick: () => {
                                            const val = compartmentInput.trim();
                                            if (val && !metadata.compartments.includes(val)) {
                                                setField('compartments', [...metadata.compartments, val]);
                                                setCompartmentInput('');
                                            }
                                        }, children: "Add" })] }), _jsx("div", { className: "flex gap-1 flex-wrap mt-2", children: metadata.compartments.map((c) => (_jsxs(Badge, { variant: "secondary", className: "cursor-pointer", onClick: () => setField('compartments', metadata.compartments.filter(x => x !== c)), children: [c, " \u00D7"] }, c))) })] })] }), typeId && _jsx(PropertiesEditor, { typeId: typeId, collection: collection }), !collection.dynamic && (_jsxs(_Fragment, { children: [_jsx(SyncMemberHeadsToggle, { collection: collection }), _jsx(SharedPropsEditor, { collection: collection })] }))] }));
}
function PropertiesEditor({ typeId, collection }) {
    const { t } = useUITranslation();
    const [formData, setFormData] = useState({});
    const toast = useToast();
    const { client } = useUserSession();
    const [isUpdating, setIsUpdating] = useState(false);
    const { data: type, error } = useFetch(() => client.store.types.catalog.resolve(typeId), [typeId]);
    const schema = type?.object_schema || {};
    const object = useMemo(() => new ManagedObject(schema, collection.properties || {}), [schema, collection.properties]);
    if (error) {
        return _jsx(ErrorBox, { title: t('store.failedToLoadType'), children: error.message });
    }
    if (!type) {
        return null;
    }
    const _onSave = (data) => {
        if (!data || !Object.keys(data).length) {
            return;
        }
        const payload = { properties: data || {} };
        setIsUpdating(true);
        client.store.collections
            .update(collection.id, payload)
            .then(() => {
            toast({
                title: t('store.collectionPropertiesUpdated'),
                description: t('store.collectionUpdatedSuccess'),
                status: "success",
                duration: 3000,
            });
        })
            .catch((err) => {
            toast({
                title: t('store.failedToUpdateCollectionProperties'),
                description: err.message,
                status: "error",
                duration: 5000,
            });
        })
            .finally(() => {
            setIsUpdating(false);
        });
    };
    const onDataChanged = (data) => {
        if (data instanceof ManagedObject) {
            setFormData(data.value);
        }
    };
    return (_jsx(Panel, { title: t('store.properties'), action: _jsx(Button, { size: "lg", isLoading: isUpdating, type: "submit", onClick: () => _onSave(formData), children: t('modal.save') }), children: _jsx(GeneratedForm, { object: object, onChange: onDataChanged }) }));
}
//# sourceMappingURL=EditCollectionView.js.map